
import React, { useState, useEffect } from 'react';
import { EyeIcon } from './icons/EyeIcon';
import { EyeOffIcon } from './icons/EyeOffIcon';

export const calculatePasswordStrength = (password: string) => {
  if (!password) {
    return { score: 0, level: '', color: 'bg-transparent', width: 'w-0' };
  }

  if (password.length < 8) {
    return { score: 1, level: 'Weak', color: 'bg-red-500', width: 'w-1/4' };
  }

  let score = 1; // Start with 1 point for length >= 8
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
  if (/\d/.test(password)) score++;
  if (/[^a-zA-Z0-9]/.test(password)) score++;

  switch (score) {
    case 1:
      return { score, level: 'Weak', color: 'bg-red-500', width: 'w-1/4' };
    case 2:
      return { score, level: 'Fair', color: 'bg-orange-500', width: 'w-1/2' };
    case 3:
      return { score, level: 'Good', color: 'bg-yellow-500', width: 'w-3/4' };
    case 4:
      return { score, level: 'Strong', color: 'bg-green-500', width: 'w-full' };
    default:
      return { score: 1, level: 'Weak', color: 'bg-red-500', width: 'w-1/4' };
  }
};

interface PasswordInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string;
  onStrengthChange?: (isStrongEnough: boolean) => void;
  showStrengthMeter?: boolean;
}

export const PasswordInput: React.FC<PasswordInputProps> = ({
  label,
  id,
  value,
  onChange,
  onStrengthChange,
  showStrengthMeter = false,
  ...rest
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [strength, setStrength] = useState(calculatePasswordStrength(value as string));

  useEffect(() => {
    const passwordValue = value as string;
    const newStrength = calculatePasswordStrength(passwordValue);
    setStrength(newStrength);
    if (onStrengthChange) {
      // Allow 'Good' (score 3) and 'Strong' (score 4)
      onStrengthChange(newStrength.score >= 3);
    }
  }, [value, onStrengthChange]);

  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-[#6B7280] mb-1">
        {label}
      </label>
      <div className="relative">
        <input
          type={showPassword ? 'text' : 'password'}
          id={id}
          value={value}
          onChange={onChange}
          {...rest}
          className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-md bg-white text-[#1F2937] focus:ring-2 focus:ring-[#4F46E5] focus:border-[#4F46E5] transition-shadow"
        />
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
          aria-label={showPassword ? 'Hide password' : 'Show password'}
        >
          {showPassword ? <EyeOffIcon /> : <EyeIcon />}
        </button>
      </div>
      {showStrengthMeter && value ? (
        <div className="mt-2">
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all duration-300 ${strength.color} ${strength.width}`}
            ></div>
          </div>
          <p className={`text-xs mt-1 font-medium ${
            strength.score < 2 ? 'text-red-500' : 
            strength.score === 2 ? 'text-orange-500' : 
            strength.score === 3 ? 'text-yellow-600' : 'text-green-600'
          }`}>
            {strength.level && `Strength: ${strength.level}`}
          </p>
        </div>
      ) : showStrengthMeter ? (
         <div className="mt-2 h-2 bg-gray-200 rounded-full"></div>
      ): null}
    </div>
  );
};